import { useRef, useState, useEffect, useMemo } from "react";
import { parseCsvFile } from "@/lib/csv";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, X, FileSpreadsheet } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const TEMPLATE = "num_socio,nombre," + Array.from({ length: 18 }, (_, i) => `H${i + 1}`).join(",") + ",estado" +
  "\n1001,Juan Perez,4,5,3,4,4,3,5,4,4,4,3,5,4,4,3,4,5,4,\n,Maria Garcia,5,4,4,3,5,4,4,4,3,4,4,4,3,5,4,4,4,5,\n,Pedro Ruiz,,,,,,,,,,,,,,,,,,,NS\n";

const stripAccents = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const normKey = (h) => stripAccents((h || "").toLowerCase()).replace(/[\s_]+/g, "");
const normName = (s) => stripAccents(String(s || "").toLowerCase()).replace(/[.,/]/g, " ").replace(/\s+/g, " ").trim();
const isNumeric = (s) => /^\d+$/.test(String(s || "").trim());

const ID_NUM = new Set(["numsocio", "socio", "membernumber", "member", "num"]);
const ID_NAME = new Set(["nombre", "name", "jugador"]);
const ID_STATUS = new Set(["estado", "status", "resultado"]);
const holeIndex = (h) => {
  const m = normKey(h).match(/^h?(?:oyo)?0?(\d{1,2})$/);
  if (!m) return null;
  const n = parseInt(m[1]);
  return n >= 1 && n <= 18 ? n - 1 : null;
};
const parseStatus = (v) => {
  const s = String(v || "").trim().toUpperCase();
  return s === "NS" || s === "DQ" ? s : "";
};

export default function ScoreCsvImport({ tid, cats, defaultRound, defaultCategoryId, onImported }) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [roundNum, setRoundNum] = useState(String(defaultRound || 1));
  const [categoryId, setCategoryId] = useState(defaultCategoryId || cats[0]?.category_id || "");
  const [applying, setApplying] = useState(false);
  const [enrolled, setEnrolled] = useState(null);

  const selectedCat = cats.find((c) => c.category_id === categoryId);
  const maxRounds = selectedCat?.rounds || 1;

  useEffect(() => {
    if (open) { setRoundNum(String(defaultRound || 1)); if (defaultCategoryId) setCategoryId(defaultCategoryId); }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch(`${API}/club/tournaments/${tid}/players`, { credentials: "include", headers: AUTH() });
        if (res.ok) {
          const pls = await res.json();
          const catName = selectedCat?.name;
          setEnrolled(new Set(pls.filter((p) => !catName || p.category_name === catName).map((p) => normName(p.name))));
        }
      } catch { setEnrolled(null); }
    })();
  }, [open, categoryId, tid]); // eslint-disable-line

  const parseFile = (file) => {
    if (!file) return;
    parseCsvFile(file, { transformHeader: (h) => (h || "").trim() }).then((res) => {
        const fields = res.meta.fields || [];
        let numCol = null, nameCol = null, statusCol = null;
        const holeCols = {}; // field -> index
        fields.forEach((f) => {
          const k = normKey(f);
          if (ID_NUM.has(k)) numCol = f;
          else if (ID_NAME.has(k)) nameCol = f;
          else if (ID_STATUS.has(k)) statusCol = f;
          else { const hi = holeIndex(f); if (hi != null) holeCols[f] = hi; }
        });
        if (Object.keys(holeCols).length === 0) { toast.error("No se detectaron columnas de hoyos (H1..H18)"); return; }
        const parsed = (res.data || []).map((r, i) => {
          const holes = Array(18).fill("");
          Object.entries(holeCols).forEach(([f, idx]) => {
            holes[idx] = (r[f] ?? "").toString().trim().toUpperCase();
          });
          const member_number = numCol ? (r[numCol] || "").toString().trim() : "";
          const name = nameCol ? (r[nameCol] || "").toString().trim() : "";
          const result_status = statusCol ? parseStatus(r[statusCol]) : "";
          return { _i: i, member_number, name, holes, result_status };
        }).filter((r) => r.member_number || r.name);
        if (parsed.length === 0) { toast.error("CSV sin filas válidas"); return; }
        setRows(parsed);
      }).catch(() => toast.error("No se pudo leer el CSV"));
  };

  const rowMatched = (r) => {
    if (!enrolled) return true;
    if (isNumeric(r.member_number)) return true;
    return enrolled.has(normName(r.name)) || enrolled.has(normName(r.member_number));
  };
  const unmatchedCount = useMemo(() => (rows || []).filter((r) => !rowMatched(r)).length, [rows, enrolled]); // eslint-disable-line
  const rowTotal = (r) => r.holes.reduce((a, h) => a + (parseInt(h) || 0), 0);
  const rowFilled = (r) => r.holes.filter((h) => h !== "").length;

  const apply = async () => {
    if (!categoryId && cats.length > 0) return toast.error("Selecciona una categoría");
    setApplying(true);
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/scores/import-csv`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          round_num: parseInt(roundNum), category_id: categoryId || null,
          rows: rows.map((r) => ({ member_number: r.member_number, name: r.name, result_status: r.result_status || null,
            holes: r.holes.map((h) => (h === "" ? null : (h === "X" ? "X" : parseInt(h)))) })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al importar");
      toast.success(`${data.applied} score(s) importado(s) para la Ronda ${roundNum}`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} omitido(s): ${data.skipped.slice(0, 3).map((s) => s.reason).join("; ")}${data.skipped.length > 3 ? "…" : ""}`);
      setRows(null); setOpen(false); onImported?.();
    } catch (e) { toast.error(e.message); }
    finally { setApplying(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "plantilla_scores.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="score-csv-btn">
        <FileSpreadsheet className="w-4 h-4 mr-1" /> Importar scores CSV
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="score-csv-modal">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-bold text-[#0F172A]">Importar scores por CSV (hoyo por hoyo)</h3>
              <button onClick={() => { setOpen(false); setRows(null); }} data-testid="score-csv-close"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="px-5 py-4 overflow-auto flex-1 space-y-4">
              <div className="flex flex-wrap gap-3 items-end">
                {cats.length > 0 && (
                  <div>
                    <Label className="text-xs">Categoría</Label>
                    <Select value={categoryId} onValueChange={setCategoryId}>
                      <SelectTrigger className="h-9 w-48 text-sm" data-testid="score-csv-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>{cats.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Día / Ronda</Label>
                  <Select value={roundNum} onValueChange={setRoundNum}>
                    <SelectTrigger className="h-9 w-28 text-sm" data-testid="score-csv-round"><SelectValue /></SelectTrigger>
                    <SelectContent>{Array.from({ length: maxRounds }, (_, i) => i + 1).map((r) => <SelectItem key={r} value={String(r)}>Ronda {r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                  onChange={(e) => { parseFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="score-csv-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="score-csv-upload"><Upload className="w-3.5 h-3.5 mr-1" /> Subir archivo</Button>
                <button onClick={downloadTemplate} className="text-xs text-[#1B4D3E] font-medium flex items-center gap-1 hover:underline mb-2" data-testid="score-csv-template"><Download className="w-3.5 h-3.5" /> Plantilla</button>
              </div>

              {!rows ? (
                <p className="text-xs text-[#64748B]">Columnas: <code>num_socio</code> o <code>nombre</code> + <code>H1..H18</code> + opcional <code>estado</code> (NS/DQ). En categorías Stableford puedes poner <code>X</code> en un hoyo si el jugador se levantó (0 puntos). Deja vacío un hoyo si no se jugó.</p>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-2 text-xs flex-wrap">
                    <span className="text-[#64748B]">{rows.length} jugador(es)</span>
                    {unmatchedCount > 0 && <span className="text-red-700 bg-red-50 rounded px-2 py-1" data-testid="score-csv-unmatched">⚠️ {unmatchedCount} sin coincidir con inscritos de {selectedCat?.name} (se omitirán)</span>}
                  </div>
                  <div className="overflow-x-auto border rounded-lg" data-testid="score-csv-preview">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50 border-b text-[#64748B]">
                        <th className="px-2 py-1 text-left">Jugador</th>
                        {Array.from({ length: 18 }, (_, i) => <th key={i} className="px-1 text-center">{i + 1}</th>)}
                        <th className="px-2 text-center font-bold">Tot</th>
                      </tr></thead>
                      <tbody>
                        {rows.map((r) => {
                          const ok = rowMatched(r);
                          return (
                            <tr key={r._i} className={`border-b last:border-b-0 ${ok ? "" : "bg-red-50"}`} data-testid={`score-csv-row-${r._i}`}>
                              <td className={`px-2 py-1 whitespace-nowrap ${ok ? "text-[#0F172A]" : "text-red-600 font-medium"}`}>
                                {r.name || r.member_number}{!ok ? " ⚠️" : ""}
                                {r.result_status && <span className={`ml-1 text-[9px] px-1 py-0.5 rounded font-bold ${r.result_status === "DQ" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>{r.result_status}</span>}
                              </td>
                              {r.holes.map((h, i) => <td key={i} className="px-1 text-center">{h || "·"}</td>)}
                              <td className="px-2 text-center font-bold">{rowTotal(r) || "—"}{rowFilled(r) < 18 ? <span className="text-amber-500">*</span> : ""}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-[#94A3B8] mt-1">* tarjeta parcial (menos de 18 hoyos)</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t">
              <Button size="sm" variant="outline" onClick={() => { setOpen(false); setRows(null); }} data-testid="score-csv-cancel">Cancelar</Button>
              <Button size="sm" disabled={!rows || applying} onClick={apply} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="score-csv-apply">
                {applying ? "Importando..." : "Importar scores"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
